var _ExtensionBuilder_confMdCbs, _ExtensionBuilder_nodeSpecs, _ExtensionBuilder_markSpecs, _ExtensionBuilder_plugins, _ExtensionBuilder_actions;
import { __classPrivateFieldGet } from "tslib";
import OrderedMap from 'orderedmap'; // eslint-disable-line import/no-extraneous-dependencies
import { inputRules } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
var Priority;
(function (Priority) {
    Priority[Priority["Highest"] = 1000000] = "Highest";
    Priority[Priority["VeryHigh"] = 100000] = "VeryHigh";
    Priority[Priority["High"] = 10000] = "High";
    Priority[Priority["Medium"] = 1000] = "Medium";
    Priority[Priority["Low"] = 100] = "Low";
    Priority[Priority["VeryLow"] = 10] = "VeryLow";
    Priority[Priority["Lowest"] = 0] = "Lowest";
})(Priority || (Priority = {}));
const DEFAULT_PRIORITY = Priority.Medium;
export class ExtensionBuilder {
    constructor(context) {
        this.Priority = ExtensionBuilder.Priority;
        /** @deprecated use `builder.Priority` instead */
        this.PluginPriority = ExtensionBuilder.PluginPriority;
        /* eslint-enable @typescript-eslint/member-ordering */
        _ExtensionBuilder_confMdCbs.set(this, []);
        _ExtensionBuilder_nodeSpecs.set(this, {});
        _ExtensionBuilder_markSpecs.set(this, {});
        _ExtensionBuilder_plugins.set(this, []);
        _ExtensionBuilder_actions.set(this, []);
        this.context = context !== null && context !== void 0 ? context : ExtensionBuilder.createContext();
    }
    static createContext() {
        return new Map();
    }
    use(extension, ...params) {
        extension(this, ...params);
        return this;
    }
    configureMd(cb) {
        __classPrivateFieldGet(this, _ExtensionBuilder_confMdCbs, "f").push(cb);
        return this;
    }
    addNode(name, cb) {
        if (__classPrivateFieldGet(this, _ExtensionBuilder_nodeSpecs, "f")[name]) {
            throw new Error(`ProseMirror node with this name "${name}" already exist`);
        }
        __classPrivateFieldGet(this, _ExtensionBuilder_nodeSpecs, "f")[name] = { name, cb };
        return this;
    }
    addMark(name, cb, priority = DEFAULT_PRIORITY) {
        if (__classPrivateFieldGet(this, _ExtensionBuilder_markSpecs, "f")[name]) {
            throw new Error(`ProseMirror mark with this name "${name}" already exist`);
        }
        __classPrivateFieldGet(this, _ExtensionBuilder_markSpecs, "f")[name] = { name, cb, priority };
        return this;
    }
    addPlugin(cb, priority = DEFAULT_PRIORITY) {
        __classPrivateFieldGet(this, _ExtensionBuilder_plugins, "f").push({ cb, priority });
        return this;
    }
    addKeymap(cb, priority = DEFAULT_PRIORITY) {
        __classPrivateFieldGet(this, _ExtensionBuilder_plugins, "f").push({ cb: (...args) => keymap(cb(...args)), priority });
        return this;
    }
    addInputRules(cb, priority = DEFAULT_PRIORITY) {
        __classPrivateFieldGet(this, _ExtensionBuilder_plugins, "f").push({ cb: (...args) => inputRules(cb(...args)), priority });
        return this;
    }
    addAction(name, cb) {
        if (__classPrivateFieldGet(this, _ExtensionBuilder_actions, "f").some(([actionName]) => actionName === name)) {
            throw new Error(`[YFM Editor] action with this name "${name}" already exist`);
        }
        __classPrivateFieldGet(this, _ExtensionBuilder_actions, "f").push([name, cb]);
        return this;
    }
    build() {
        const confMd = __classPrivateFieldGet(this, _ExtensionBuilder_confMdCbs, "f").slice();
        const nodes = Object.assign({}, __classPrivateFieldGet(this, _ExtensionBuilder_nodeSpecs, "f"));
        const marks = Object.assign({}, __classPrivateFieldGet(this, _ExtensionBuilder_markSpecs, "f"));
        const plugins = __classPrivateFieldGet(this, _ExtensionBuilder_plugins, "f").slice();
        const actions = __classPrivateFieldGet(this, _ExtensionBuilder_actions, "f").slice();
        return {
            configureMd: (md) => confMd.reduce((pMd, cb) => cb(pMd), md),
            nodes: () => {
                let map = OrderedMap.from({});
                for (const { name, cb } of Object.values(nodes)) {
                    map = map.addToEnd(name, cb());
                }
                return map;
            },
            marks: () => {
                // The order of marks in schema is important when serializing pm-document to DOM or markup
                // https://discuss.prosemirror.net/t/marks-priority/4463
                const sortedMarks = Object.values(marks).sort((a, b) => b.priority - a.priority);
                let map = OrderedMap.from({});
                for (const { name, cb } of sortedMarks) {
                    map = map.addToEnd(name, cb());
                }
                return map;
            },
            plugins: (deps) => {
                return plugins
                    .sort((a, b) => b.priority - a.priority)
                    .reduce((acc, { cb }) => {
                    const res = cb(deps);
                    if (Array.isArray(res))
                        acc.push(...res);
                    else
                        acc.push(res);
                    return acc;
                }, []);
            },
            actions: (deps) => actions.reduce((obj, [name, cb]) => {
                obj[name] = cb(deps);
                return obj;
            }, {}),
        };
    }
}
_ExtensionBuilder_confMdCbs = new WeakMap(), _ExtensionBuilder_nodeSpecs = new WeakMap(), _ExtensionBuilder_markSpecs = new WeakMap(), _ExtensionBuilder_plugins = new WeakMap(), _ExtensionBuilder_actions = new WeakMap();
/* eslint-disable @typescript-eslint/member-ordering */
ExtensionBuilder.Priority = Priority;
/** @deprecated use `ExtensionBuilder.Priority` instead */
ExtensionBuilder.PluginPriority = ExtensionBuilder.Priority;
